import React, { useContext, useEffect, useMemo } from 'react';

import { FormikProps } from 'formik';

// components
import AampeStepperComponent from '../../components/shared/AampeStepper/AampeStepperComponent';

// constants
import { CONTROLLED_FORMULA_BUFFER } from '../../constants/formula';

// context
import { EditorContext } from '../../context/EditorContext';

// helpers
import { updateEditorDetailsFromFormikValues } from '../../helpers/formulaHelpers';
import { isScheduleTimeInPast } from '../../helpers/formulaHelpersPure';

// models
import {
    FormulaViewSetupFormValue,
    ProcessedFormulaV1,
} from '../../models/formula.model';
import { AampeStepperStepProps } from '../../models/stepper.model';

import FormulaFinalCheck from '../formulaCreate/FormulaFinalCheck';
import FormulaSetup from '../formulaCreate/FormulaSetup';
import FormulaDeliverySettingsWrapper from '../formulaCreate/formulaSettings/FormulaDeliverySettingsWrapper';
import FormulaEditViewActions from './FormulaEditViewActions';

interface FormulaEditViewProps {
    formikProps: FormikProps<FormulaViewSetupFormValue>;
    setIsEditing: (value: boolean) => void;
    isFormSubmitting?: boolean;
    data?: ProcessedFormulaV1;
    hasWriteAccess: boolean;
}

const FormulaEditView: React.FC<React.PropsWithChildren<FormulaEditViewProps>> =
    ({ setIsEditing, formikProps, isFormSubmitting, data, hasWriteAccess }) => {
        const { setEditorDetails } = useContext(EditorContext);
        const onSave = () => {
            formikProps?.handleSubmit();
        };

        useEffect(() => {
            updateEditorDetailsFromFormikValues(
                formikProps.values,
                setEditorDetails,
            );
        }, [formikProps.values, setEditorDetails]);

        const steps = useMemo<AampeStepperStepProps[]>(
            () => [
                {
                    title: 'Setup',
                    tabKey: 'setup',
                    renderComponent: (
                        <FormulaSetup
                            formikProps={formikProps}
                            hasWriteAccess={hasWriteAccess}
                        />
                    ),
                    name: 'Message setup',
                    getCompletionStatus: () => !!formikProps?.values?.name,
                },
                {
                    title: 'Scheduling',
                    tabKey: 'scheduling',
                    renderComponent: (
                        <FormulaDeliverySettingsWrapper
                            formikProps={formikProps}
                            liveData={data}
                        />
                    ),
                    getCompletionStatus: () => {
                        if (formikProps.values?.is_controlled_message) {
                            return !isScheduleTimeInPast(
                                formikProps.values?.send_start as string,
                                CONTROLLED_FORMULA_BUFFER,
                            );
                        }
                        return true;
                    },
                },
                {
                    title: 'Final check',
                    tabKey: 'final-check',
                    renderComponent: (
                        <FormulaFinalCheck
                            isFormulaView
                            formikProps={formikProps}
                        />
                    ),
                },
            ],
            [formikProps, data, hasWriteAccess],
        );

        const isValidated = useMemo<boolean>(
            () =>
                !steps.every(
                    (step) =>
                        (step.getCompletionStatus &&
                            step.getCompletionStatus()) ||
                        !step.getCompletionStatus,
                ),
            [steps],
        );

        return (
            <AampeStepperComponent
                steps={steps}
                showStepsAsCompleted
                rightActionButtonsForLastStep={
                    <FormulaEditViewActions
                        onSave={onSave}
                        onCancel={() => setIsEditing?.(false)}
                        isLoading={isFormSubmitting}
                        disabled={isValidated}
                    />
                }
            />
        );
    };

export default FormulaEditView;
